import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@radix-ui/react-popover";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { FormMessage } from "../ui/form";
import { previewTimeFormatter } from "./utils";

export function TimePicker({
	value,
	onChange,
	disabled,
	error,
}: {
	value?: string;
	onChange: (time: string) => void;
	disabled?: boolean;
	error?: string;
}) {
	const hours = Array.from({ length: 12 }, (_, i) => i + 1);
	const minutes = Array.from({ length: 12 }, (_, i) => i * 5);
	const periods = ["AM", "PM"];

	const [selectedHour, selectedMinute, selectedPeriod] = value
		? convert24to12(value)
		: [undefined, undefined, undefined];

	function convert24to12(time24: string): [number, number, string] {
		const [hours24, minutes] = time24.split(":").map(Number);
		const period = hours24 >= 12 ? "PM" : "AM";
		const hours12 = hours24 % 12 || 12;
		return [hours12, minutes, period];
	}

	function convert12to24(
		hours: number,
		minutes: number,
		period: string,
	): string {
		let hours24 = hours;
		if (period === "PM" && hours !== 12) hours24 += 12;
		if (period === "AM" && hours === 12) hours24 = 0;
		return `${hours24.toString().padStart(2, "0")}:${minutes
			.toString()
			.padStart(2, "0")}`;
	}

	return (
		<Popover>
			<PopoverTrigger asChild>
				<div className="flex flex-col space-y-1">
					<Button
						variant="outline"
						className={cn(
							"w-32 justify-start text-left font-normal h-12",
							"bg-transparent hover:bg-transparent hover:text-white border-white/10 text-white rounded-xl",
							"hover:border-white/20 transition-colors",
							!value && "text-white/50",
							disabled && "opacity-50 cursor-not-allowed",
						)}
						disabled={disabled}
					>
						{value ? (
							previewTimeFormatter.format(new Date(`2000-01-01T${value}`))
						) : (
							<span>Set time</span>
						)}
					</Button>
					{error && (
						<FormMessage className="text-red-500 text-sm">{error}</FormMessage>
					)}
				</div>
			</PopoverTrigger>
			<PopoverContent
				className="w-[280px] p-0 bg-black border border-muted-light/20 z-[100] rounded-md mt-1"
				align="start"
			>
				<div className="p-4">
					<div className="flex gap-4">
						{/* Hours */}
						<div className="flex-1">
							<div className="text-sm font-medium text-white/70 mb-2">Hour</div>
							<div className="grid grid-cols-3 gap-2">
								{hours.map((hour) => (
									<Button
										key={hour}
										variant="outline"
										className={cn(
											"h-9 rounded-md border-white/10 bg-[#0F2231] text-white hover:text-accent-foreground hover:bg-accent",
											selectedHour === hour &&
												"bg-accent !text-accent-foreground hover:bg-accent hover:!text-accent-foreground focus:bg-accent focus:!text-accent-foreground",
										)}
										onClick={() => {
											if (selectedMinute !== undefined && selectedPeriod) {
												onChange(
													convert12to24(hour, selectedMinute, selectedPeriod),
												);
											} else {
												onChange(
													convert12to24(hour, 0, selectedPeriod || "AM"),
												);
											}
										}}
									>
										{hour}
									</Button>
								))}
							</div>
						</div>

						{/* Minutes */}
						<div className="flex-1">
							<div className="text-sm font-medium text-white/70 mb-2">
								Minute
							</div>
							<div className="grid grid-cols-3 gap-2">
								{minutes.map((minute) => (
									<Button
										key={minute}
										variant="outline"
										className={cn(
											"h-9 rounded-md border-white/10 bg-[#0F2231] text-white hover:text-accent-foreground hover:bg-accent",
											selectedMinute === minute &&
												"bg-accent !text-accent-foreground hover:bg-accent hover:!text-accent-foreground focus:bg-accent focus:!text-accent-foreground",
										)}
										onClick={() => {
											if (selectedHour !== undefined && selectedPeriod) {
												onChange(
													convert12to24(selectedHour, minute, selectedPeriod),
												);
											} else {
												onChange(
													convert12to24(12, minute, selectedPeriod || "AM"),
												);
											}
										}}
									>
										{minute.toString().padStart(2, "0")}
									</Button>
								))}
							</div>
						</div>
					</div>

					{/* AM/PM */}
					<div className="mt-4">
						<div className="text-sm font-medium text-white/70 mb-2">Period</div>
						<div className="grid grid-cols-2 gap-2">
							{periods.map((period) => (
								<Button
									key={period}
									variant="outline"
									className={cn(
										"h-9 rounded-md border-white/10 bg-[#0F2231] text-white hover:text-accent-foreground hover:bg-accent",
										selectedPeriod === period &&
											"bg-accent !text-accent-foreground hover:bg-accent hover:!text-accent-foreground focus:bg-accent focus:!text-accent-foreground",
									)}
									onClick={() => {
										if (selectedHour !== undefined) {
											onChange(
												convert12to24(
													selectedHour,
													selectedMinute || 0,
													period,
												),
											);
										} else {
											onChange(convert12to24(12, selectedMinute || 0, period));
										}
									}}
								>
									{period}
								</Button>
							))}
						</div>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}
